/**
 * Footer — Global footer with legal + security links (Phase 19.4 audit-fix 2026-04-29)
 *
 * Per scope §2 19.3 + §6 line 218: must be mounted on every page (public +
 * authenticated) so legal/security links are 1 click from anywhere.
 * GDPR Article 13 transparency requirement.
 *
 * Design:
 *   - Compact 1-row strip (not multi-column) — visually unobtrusive on
 *     authenticated dashboards but legally complete
 *   - Mobile: wraps to multi-row (flex-wrap)
 *   - Color-tone matches the parent layout (var() tokens)
 *   - 12px font, monospace — legal-doc-feel
 *   - All 6 legal links + 2 security mailboxes + status link
 *
 * Mounting (Phase 19 + Phase 19 Tier-2 §2.2):
 *   - AdminLayout (admin/SPL/god) — variant: default
 *   - AnnotatorLayout (annotator) — variant: default
 *   - ClientPortal (client) — variant: default
 *   - LegalLayout (all /legal/* pages: MSA, Terms, Privacy, DPA, SLA) — variant: default
 *   - Login / Signup — variant: auth
 *   - Phase 19 Tier-2 §2.2 additions:
 *     - RequestAccess (public lead-capture; GDPR Article 13 PII-collection point)
 *     - PasswordResetRequest / PasswordResetConfirm — variant: auth
 *     - ClientAcceptInvite / ClientInviteConfirm — variant: auth
 *     - Onboarding / OrgSetupWizard — variant: auth
 *     - Security / Disclosure / Acknowledgments — variant: default
 *     - Subprocessors — variant: default
 *     - NotFoundPage (App.jsx) — variant: auth
 *     - AppCrashFallback (main.jsx) — inline minimal links (Footer.jsx can't be
 *       mounted before React tree finishes; inline static <a> tags instead)
 *   - Already-on-landing-page colophon serves as the public-marketing equivalent
 *
 * Phase 19 audit-fix 2026-04-29 — closes BLOCKER from forensic audit.
 * Phase 19 Tier-2 2026-04-30 — extends mount to 14+ routes; closes Agent B
 * adversarial finding A (12+ customer-facing routes were missing Footer).
 */

export default function Footer({ variant = 'default' }) {
  // variant: 'default' | 'compact' | 'auth' (login/signup/etc — minimal)
  const isAuth = variant === 'auth';
  const isCompact = variant === 'compact';

  return (
    <footer
      role="contentinfo"
      aria-label="Site footer with legal and security links"
      style={{
        padding: isCompact ? '12px 16px' : '18px 24px',
        marginTop: 'auto',
        borderTop: '1px solid var(--border, rgba(0,0,0,0.08))',
        background: isAuth ? 'transparent' : 'var(--bg-card, transparent)',
        fontSize: 12,
        fontFamily: 'var(--font-mono, monospace)',
        color: 'var(--text-muted, #8e8575)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'center',
        justifyContent: 'space-between',
        letterSpacing: '0.02em',
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
        <span style={{ opacity: 0.7 }}>JUUNI · 十二 · 2026</span>
        {!isCompact && (
          <>
            <FooterLink href="/security">Security</FooterLink>
            <FooterLink href="/legal/privacy">Privacy</FooterLink>
            <FooterLink href="/legal/terms">Terms</FooterLink>
            <FooterLink href="/legal/dpa">DPA</FooterLink>
            <FooterLink href="/legal/subprocessors">Subprocessors</FooterLink>
            <FooterLink href="/sla">SLA</FooterLink>
          </>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
        <FooterLink href="mailto:security@juuniai.com">security@juuniai.com</FooterLink>
        <FooterLink href="mailto:support@juuniai.com">support@juuniai.com</FooterLink>
        {!isCompact && (
          <FooterLink href="/.well-known/security.txt">security.txt</FooterLink>
        )}
      </div>
    </footer>
  );
}

function FooterLink({ href, children }) {
  return (
    <a
      href={href}
      style={{
        color: 'inherit',
        textDecoration: 'underline',
        textUnderlineOffset: 2,
        opacity: 0.85,
      }}
    >
      {children}
    </a>
  );
}
